import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import type { Session } from "@supabase/supabase-js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";

// Map background URL builder
const getMapBackgroundUrl = () => {
  const apiKey = process.env.BUN_PUBLIC_GOOGLE_API_KEY;
  const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
  const params = new URLSearchParams({
    center: '40.7128,-74.0060',
    zoom: '2',
    size: '1920x1080',
    scale: '2',
    key: apiKey || '',
  });

  // Dark theme styles - no labels
  const styles = [
    'element:geometry|color:0x1d2c4d',
    'element:labels|visibility:off',
    'feature:water|element:geometry|color:0x0e1626',
    'feature:road|element:geometry|color:0x304a7d',
    'feature:landscape|element:geometry|color:0x023e58',
  ];

  styles.forEach(style => params.append('style', style));

  return `${baseUrl}?${params.toString()}`;
};

export function Landing() {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [session, setSession] = useState<Session | null>(null);

  const mapBackgroundUrl = getMapBackgroundUrl();

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handleKeyPress = () => {
      setShowAuth(true);
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  if (showAuth && !session) {
    return (
      <div className="min-h-screen bg-background p-8 flex items-center justify-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.65] pointer-events-none"
          style={{
            backgroundImage: `url('${mapBackgroundUrl}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'grayscale(1) brightness(0.5)',
          }}
        />
        <div className="max-w-md w-full relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold font-mono text-foreground">
              <span className="text-muted-foreground">&gt;</span> Map Bldr
            </h1>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="font-mono text-sm">
                Continue with email
              </CardTitle>
              <CardDescription className="font-mono text-xs">
                Enter your email to receive a magic link
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Auth
                supabaseClient={supabase}
                appearance={{
                  theme: ThemeSupa,
                  variables: {
                    default: {
                      colors: {
                        brand: 'hsl(var(--primary))',
                        brandAccent: 'hsl(var(--primary))',
                      },
                      fonts: {
                        bodyFontFamily: 'ui-monospace, monospace',
                        buttonFontFamily: 'ui-monospace, monospace',
                        inputFontFamily: 'ui-monospace, monospace',
                        labelFontFamily: 'ui-monospace, monospace',
                      },
                    },
                  },
                  className: {
                    container: 'font-mono',
                    button: 'font-mono',
                    input: 'font-mono',
                    label: 'font-mono',
                  },
                }}
                providers={[]}
                redirectTo={window.location.origin}
                view="magic_link"
                showLinks={false}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (session) {
    return (
      <div className="min-h-screen bg-background p-8 flex items-center justify-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.65] pointer-events-none"
          style={{
            backgroundImage: `url('${mapBackgroundUrl}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'grayscale(1) brightness(0.5)',
          }}
        />
        <div className="text-center space-y-4 relative z-10">
          <h1 className="text-3xl font-bold font-mono text-foreground">
            <span className="text-muted-foreground">&gt;</span> Logged in!
          </h1>
          <p className="text-muted-foreground mt-2">
            Welcome, {session.user.email}
          </p>
          <Button onClick={() => navigate("/builder")} className="font-mono">
            Create →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.65] pointer-events-none"
        style={{
          backgroundImage: `url('${mapBackgroundUrl}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'grayscale(1) brightness(0.5)',
        }}
      />
      <div className="max-w-3xl mx-auto relative z-10 flex flex-col min-h-[calc(100vh-4rem)]">
        <div className="text-center mb-8">
          <pre className="text-xs font-mono text-muted-foreground mb-4 inline-block">
            {`
███╗   ███╗ █████╗ ██████╗     ██████╗ ██╗     ██████╗ ██████╗
████╗ ████║██╔══██╗██╔══██╗    ██╔══██╗██║     ██╔══██╗██╔══██╗
██╔████╔██║███████║██████╔╝    ██████╔╝██║     ██║  ██║██████╔╝
██║╚██╔╝██║██╔══██║██╔═══╝     ██╔══██╗██║     ██║  ██║██╔══██╗
██║ ╚═╝ ██║██║  ██║██║         ██████╔╝███████╗██████╔╝██║  ██║
╚═╝     ╚═╝╚═╝  ╚═╝╚═╝         ╚═════╝ ╚══════╝╚═════╝ ╚═╝  ╚═╝
`}
          </pre>
          <p className="text-muted-foreground mt-2 font-mono">
            A tool for GeoGuessr map creation!
          </p>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="font-mono">
                <span className="text-muted-foreground">&gt;</span> Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 font-mono text-sm">
                <li className="flex items-start">
                  <span className="text-primary mr-3">$</span>
                  <span>Tag locations automatically with model assistance</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3">$</span>
                  <span>Build maps fast with dual-view satellite and street editing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3">$</span>
                  <span>Access both official and unofficial coverage worldwide</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3">$</span>
                  <span>Save and manage your complete map collection</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="text-center text-sm text-muted-foreground font-mono pb-8">
          <p>&gt; Press any key to continue...</p>
          <p className="animate-pulse mt-2">_</p>
        </div>
      </div>
    </div>
  );
}

export default Landing;
